/**
 * MicLockdownCard — 1.133.0 structured error UI for iOS Lockdown Mode.
 *
 * Replaces the wall-of-text mediaDevices-unavailable hint with a scannable
 * 3-step card. Rendered by VoicePanel when status='error' AND error contains
 * "Lockdown Mode" (the marker phrase set by voiceStore's
 * pickMediaDevicesUnavailableHint heuristic).
 *
 * UX rationale: pre-1.133.0 the entire hint was one inline paragraph in a
 * cramped error span. On iPhone the whole message was unreadable in one
 * glance. This card puts each action on its own line, exposes the primary
 * path immediately, and hides the alternate / why-this-happens sections
 * behind disclosure toggles so the small screen isn't overwhelmed.
 *
 * Sources for the copy:
 *   - Apple support 105120 (Lockdown Mode overview, per-site exclusion).
 *   - Sevarg (2022): empirical WebKit feature-disable on Lockdown Mode.
 *   - MDN: navigator.mediaDevices === undefined when UA disables the feature.
 */
import { useState } from 'react';

export function MicLockdownCard() {
  const [altOpen, setAltOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  // 1.136.0 — let the user dismiss after reading. Local state; reload of
  // the page restores the card if the underlying condition still applies.
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <div
      data-testid="voice-lockdown-card"
      className="rounded-md border border-vibe-error bg-vibe-bg-tertiary text-vibe-text text-xs p-3 space-y-2"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start justify-between gap-2">
        <div
          data-testid="voice-lockdown-title"
          className="font-semibold text-vibe-error flex items-center gap-2"
        >
          <span aria-hidden="true">🔒</span>
          <span>Microphone blocked by iOS Lockdown Mode</span>
        </div>
        <button
          type="button"
          data-testid="voice-lockdown-close"
          aria-label="dismiss Lockdown Mode notice"
          onClick={() => setDismissed(true)}
          className="text-vibe-text-secondary hover:text-vibe-text -mr-1 -mt-1 px-2 py-1 rounded [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
        >
          ×
        </button>
      </div>

      <ol className="space-y-1 pl-4 list-decimal text-vibe-text">
        <li data-testid="voice-lockdown-step-1">
          In Safari, tap the <strong>&ldquo;•••&rdquo;</strong> button next to the URL bar.
          (iOS 17/18 or Top toolbar layout: this is the <strong>&ldquo;Aa&rdquo;</strong> button on the LEFT of the URL bar.)
        </li>
        <li data-testid="voice-lockdown-step-2">
          Tap <strong>&ldquo;Website Settings&rdquo;</strong>.
        </li>
        <li data-testid="voice-lockdown-step-3">
          Turn <strong>OFF</strong> Lockdown Mode for this site, then <strong>reload</strong> the page
          and tap the mic again to grant the iOS permission prompt.
        </li>
      </ol>

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          data-testid="voice-lockdown-alternate-toggle"
          onClick={() => setAltOpen((v) => !v)}
          className="text-vibe-accent underline-offset-2 hover:underline"
          aria-expanded={altOpen}
        >
          {altOpen ? 'Hide alternate path' : 'Show alternate path'}
        </button>
        <button
          type="button"
          data-testid="voice-lockdown-why-toggle"
          onClick={() => setWhyOpen((v) => !v)}
          className="text-vibe-accent underline-offset-2 hover:underline"
          aria-expanded={whyOpen}
        >
          {whyOpen ? 'Hide why' : 'Why this happens'}
        </button>
      </div>

      {altOpen && (
        <div data-testid="voice-lockdown-alternate-content" className="pt-1 text-vibe-text-secondary">
          Settings → Privacy &amp; Security → Lockdown Mode → Configure Web Browsing →
          Excluded Safari Websites → add this site.
        </div>
      )}

      {whyOpen && (
        <div data-testid="voice-lockdown-why-content" className="pt-1 text-vibe-text-secondary">
          iOS Lockdown Mode hides navigator.mediaDevices on every non-excluded site.
          That&rsquo;s why the Settings → Safari → Camera &amp; Microphone toggle
          won&rsquo;t list this site until Lockdown is excluded for it. If the page
          is loaded inside a third-party app/WebView (not Safari proper) the mic
          API is blocked entirely; open the link in Safari directly.
        </div>
      )}
    </div>
  );
}
